import { forwardRef } from "react";

import { css, cva, cx } from "~/styled-system/css";

export interface CircularProgressProps {
  progress?: number;
  size?: "xs" | "sm" | "md" | "lg";
  color?: "purple" | "gray" | "white";
  thickness?: number;
  className?: string;
}

const sizeMap = {
  xs: 20,
  sm: 24,
  md: 44,
  lg: 64,
};

const thicknessMap = {
  xs: 1.6,
  sm: 2,
  md: 3.6,
  lg: 4,
};

export const CircularProgress = forwardRef<HTMLDivElement, CircularProgressProps>(
  function CircularProgress(
    { progress = 0, size = "md", color = "gray", thickness, className, ...rest },
    ref
  ) {
    const actualSize = sizeMap[size];
    const actualThickness = thickness ?? thicknessMap[size];

    const radius = (actualSize - actualThickness) / 2;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference * ((100 - Math.min(100, Math.max(0, progress))) / 100);

    return (
      <div
        ref={ref}
        className={cx(
          circularProgressContainer,
          circularProgressStyles({ size, color }),
          className
        )}
        {...rest}
      >
        <svg
          viewBox={`0 0 ${actualSize} ${actualSize}`}
          className={svgStyles}
          style={{ transform: "rotate(-90deg)" }}
        >
          {/* Background circle */}
          <circle
            className={backgroundCircleStyles({ color })}
            cx={actualSize / 2}
            cy={actualSize / 2}
            r={radius}
            fill="none"
            strokeWidth={actualThickness}
          />
          {/* Progress circle */}
          <circle
            className={progressCircleStyles({ color })}
            cx={actualSize / 2}
            cy={actualSize / 2}
            r={radius}
            fill="none"
            strokeWidth={actualThickness}
            strokeLinecap="round"
            style={{
              strokeDasharray: `${circumference}px`,
              strokeDashoffset: `${strokeDashoffset}px`,
            }}
          />
        </svg>
      </div>
    );
  }
);

const circularProgressContainer = css({
  display: "inline-flex",
  position: "relative",
});

const svgStyles = css({
  display: "block",
});

const backgroundCircleStyles = cva({
  variants: {
    color: {
      purple: { stroke: "rgba(123, 97, 255, 0.2)" },
      gray: { stroke: "$gray.900" },
      white: { stroke: "$gray.800" },
    },
  },
  defaultVariants: {
    color: "purple",
  },
});

const progressCircleStyles = cva({
  base: {
    transition: "stroke-dashoffset 0.3s ease",
  },
  variants: {
    color: {
      purple: { stroke: "$purple" },
      gray: { stroke: "$gray.400" },
      white: { stroke: "white" },
    },
  },
  defaultVariants: {
    color: "purple",
  },
});

const circularProgressStyles = cva({
  variants: {
    size: {
      xs: { width: "20px", height: "20px" },
      sm: { width: "24px", height: "24px" },
      md: { width: "44px", height: "44px" },
      lg: { width: "64px", height: "64px" },
    },
    color: {
      purple: {},
      gray: {},
      white: {},
    },
  },
  defaultVariants: {
    size: "md",
    color: "purple",
  },
});
